import { canConstruct } from 'core-cmp/can'
import { APP } from 'core-uda/model/Resource'
import { THM_PREF } from 'thm/ThmPreferences'
import _ from 'core-cmp/lodash'
import { RIGHTS } from 'core-uda/Rights'
import DECKGL from 'core-cls-gis-deckgl'
import { LAYER_BUILDERS } from 'core-gis/ol/layer/OLLayerCreator'
import { STYLE_RESOLVER } from 'core-cls-gis-style/StyleResolver'
import GeotiffConstants from 'core-geotiff/utils/GeotiffConstants'
import { loadLayerConfigFromYaml } from 'core-gis/ol/layerConfig'
import { deprecatedRoadLayer } from './deprecated/deprecatedRoadLayer'
import { deprecatedMarineLayer } from './deprecated/deprecatedMarineLayer'
import { deprecatedImageryLayer } from './deprecated/deprecatedImageryLayer'
import { deprecatedHybridLayer } from './deprecated/deprecatedHybridLayer'
import { deprecatedBathymetryLayer } from './deprecated/deprecatedBathymetryLayer'
import { deprecatedSouthPoleCCAMLRLayer } from './deprecated/deprecatedSouthPoleCCAMLRLayer'
import { deprecatedNorthPoleLayer } from './deprecated/deprecatedNorthPoleLayer'
import { deprecatedSouthPoleLayer } from './deprecated/deprecatedSouthPoleLayer'
import MultiLayerData from 'core-metoc/model/multiLayer/MultiLayerData'

DECKGL.register({ GIS, LAYER_BUILDERS, STYLE_RESOLVER })

// Allows to save rendering of metoc data
MultiLayerData.saveState = true

const BASE_LAYERS = {
  road: {}, // from center parameter using Yaml
  marine: {}, // from center parameter using Yaml
  ordonanceSurveyUk: {}, // from center parameter using Yaml
  roadEvfr: {}, // from center parameter using Yaml
  roadOsni: {}, // from center parameter using Yaml
  admiralty: {}, // from center parameter using Yaml
  imagery: {}, // from center parameter using Yaml
  hybrid: {}, // from center parameter using Yaml
  bathymetry: {}, // from center parameter using Yaml
  southPoleCCAMLR: {}, // from center parameter using Yaml
  northPole: {}, // from center parameter using Yaml
  southPole: {}, // from center parameter using Yaml
}

const VIEW_LAYERS = {
  GmapRoad: {
    type: 'baseView',
    typeId: 'ROADMAP', // Type google (google.maps.MapTypeId)
    icon: 'road',
    label: 'Road',
  },
  GmapSatellite: {
    type: 'baseView',
    typeId: 'SATELLITE', // Type google (google.maps.MapTypeId)
    icon: 'satellite',
    label: 'Imagery',
  },
  GmapHybrid: {
    type: 'baseView',
    typeId: 'HYBRID', // Type google (google.maps.MapTypeId)
    icon: 'hybrid',
    label: 'Hybrid',
  },
}

const OVERLAY_LAYERS = [
  'nightAndDay',
  'grid',
  'scaleLine',
  'metarTaf',
  {
    id: 'eez',
    type: 'tileWMS',
    url: 'TO SET',
    data: { icon: 'zones' },
    params: {
      LAYERS: 'cls:eez',
    },
    proj: 'EPSG:3857',
    tileSize: 512,
    maxZoom: 19,
    minZoom: 2,
  },
  {
    id: 'ices',
    type: 'tileWMS',
    url: 'TO SET',
    data: { icon: 'zones' },
    params: {
      LAYERS: 'cls:ices',
    },
    proj: 'EPSG:3857',
    tileSize: 1024,
    maxZoom: 19,
    minZoom: 2,
  },
  {
    id: 'fao',
    type: 'tileWMS',
    url: 'TO SET',
    data: { icon: 'zones' },
    params: {
      LAYERS: 'cls:fao',
    },
    proj: 'EPSG:3857',
    tileSize: 1024,
    maxZoom: 19,
    minZoom: 2,
  },
  {
    id: 'geotiffs',
    data: {},
    type: 'group',
    // At the init, 8 layers without source for the moment (data is async retrieved somewhere else)
    layers: [...new Array(GeotiffConstants.MAX_NB_GEOTIFFS)].map((v, idx) => ({
      type: 'staticImage',
      id: `${GeotiffConstants.LAYER_PREFIX}${idx}`,
      data: { id: `${GeotiffConstants.LAYER_PREFIX}${idx}` },
    })),
  },
]

const DECK_LAYER = {
  type: 'deckgl',
  id: 'deck',
  deckLayers: [
    {
      id: 'driftZones', // study zone, Ex : zone sargasse
      type: 'Polygon',
      pickable: true,
    },
    {
      id: 'driftInitialZones',
      type: 'GeometryCollection', // Polygon, lines or points
      pickable: true,
    },
    {
      id: 'driftTrajectories',
      type: 'LineString',
      pickable: true,
    },
    {
      id: 'driftInitialParticles',
      type: 'Point',
      pickable: true,
    },
    {
      id: 'driftParticles',
      type: 'Point',
      pickable: true,
    },
    {
      id: 'searchArea_line',
      type: 'LineString',
      pickable: false,
    },
    {
      id: 'searchArea_icon',
      type: 'Icon',
      pickable: false,
    },
    {
      id: 'searchArea_label_text',
      type: 'text',
      pickable: false,
      outlineWidth: 1,
    },
    {
      id: 'searchArea_angle_text',
      type: 'text',
      pickable: false,
    },
    {
      id: 'sarSru_searchArea_line',
      type: 'LineString',
      pickable: false,
    },
    {
      id: 'sarSru_searchArea_icon',
      type: 'Icon',
      pickable: false,
    },
    {
      id: 'sarSru_searchArea_angle_text',
      type: 'text',
      pickable: false,
    },
    {
      id: 'sarSru_searchArea_label_text',
      type: 'text',
      pickable: false,
      outlineWidth: 1,
    },
    {
      id: 'sarOilDriftSurfaceThicknesses',
      type: 'Polygon',
      pickable: true,
    },
    {
      id: 'sarOilDriftImpactedAreas',
      type: 'Polygon',
      pickable: true,
    },
    {
      id: 'sarOilDriftProbabilities',
      type: 'Polygon',
      pickable: true,
    },
    {
      id: 'sarOilDriftMinTimes',
      type: 'Polygon',
      pickable: true,
    },
    {
      id: 'sarOilDriftTrajectories',
      type: 'LineString',
      pickable: true,
    },
    {
      id: 'sarOilDriftParticles',
      type: 'Point',
      pickable: true,
    },
    {
      id: 'sarOilDriftCenterOfMassTrajectories',
      type: 'LineString',
      pickable: true,
    },
    {
      id: 'sarOilDriftBooms',
      type: 'LineString',
      pickable: true,
    },
    {
      id: 'sarOilDriftDispersantRectangles',
      type: 'LineString',
      pickable: true,
    },
    {
      id: 'sarOilDriftDispersantTracks',
      type: 'LineString',
      pickable: true,
    },
    {
      id: 'sarOilDriftSkimmerTracks',
      type: 'LineString',
      pickable: true,
    },
    {
      id: 'sarOilDriftCenterOfMass',
      type: 'Point',
      pickable: true,
    },
    {
      id: 'sarOilDriftDispersantDepartures',
      type: 'Icon',
      pickable: true,
    },
    {
      id: 'sarOilDriftSkimmerDepartures',
      type: 'Icon',
      pickable: true,
    },
    {
      id: 'sarOilDriftLeaks',
      type: 'Icon',
      pickable: true,
    },
    {
      id: 'sarVoyage',
      type: 'LineString',
      pickable: false,
    },
    {
      id: 'sarTrack',
      type: 'LineString',
      pickable: true,
    },
    {
      id: 'sarTrackArrows',
      type: 'Icon',
      pickable: false,
    },
    {
      id: 'sarEntry',
      type: 'Icon',
      pickable: false,
    },
    {
      id: 'sarSruPosition',
      type: 'Icon',
      pickable: false,
    },
    {
      id: 'sarSruPositionText',
      type: 'Text',
      pickable: false,
      outlineWidth: 1,
      fontWeight: 'normal',
    },
    {
      id: 'drawPolygon',
      type: 'Polygon',
      pickable: false,
    },
    {
      id: 'drawLine',
      type: 'LineString',
      pickable: false,
    },
    {
      id: 'drawIcon',
      type: 'Icon',
      pickable: false,
    },
    {
      id: 'drawText',
      type: 'text',
      pickable: false,
    },
    {
      id: 'drawOutlineText',
      type: 'text',
      pickable: false,
      outlineWidth: 1,
    },
  ],
}

// THM-13166: webgl style
const predefinedStylesParticles = {
  circles: {
    symbol: {
      symbolType: 'circle',
      size: 8,
      color: (f) => '#9B59B6',
      rotateWithView: false,
      offset: [0, 0],
      opacity: 0.6,
    },
  },
}

const VECTOR_LAYERS = [
  'heatMap',
  'gridMap',
  'backInfo',
  'radars',
  'zones',
  'selectionLayer',
  {
    id: 'mobiles',
    type: 'group',
    childIds: ['mobiles.trajectories', 'mobiles.server'],
  },
  'radarsEdit',
  'animation',
  'animationOverlay',
  'collectedDatas',
  'alerts',
  'sensorMessages',
  'sarAlertsLines',
  'sarAlerts',
  'sarSruBasePositions',
  'sarSRUnits',
  'mobidriftHeatMap',
  'meteoStationRecord',
  'mobidriftHeatMap',
  'mobidriftGridMap',
  /* DECK_LAYER, */ 'sarDrift',
  'sarOilDrift',
  'sarSad',
  'sarSac',
  'sarOperation',
  'sarSacTrajectory',
  'sarSearch',
  'sarEdition',
  'sarAnimation',
  'measures',
  'starMeasures',
  'drawMeasureLayer',
  'drawLayerWrapX',
  'drawLayer',
  'info',
  'satellites',
  'flagOnPosition',
  DECK_LAYER,
]

let ThmUmvMapConfig = canConstruct.extend({
  marineLayers: ['tileWMS', 'tileXYZ', 'WMS'],

  /**
   * Retourne la configuration pour les layers de fonds : baseLayers.
   * @returns {*|Object}
   */
  getBaseLayers: function () {
    let me = this,
      rightsLayers = RIGHTS('map.baseLayers', 'road').split(','), // Base Layers autorisés
      baseLayerPref = THM_PREF.get('baseLayer') || 'none', // Préférence pointant vers le dernier baseLayer actif
      baseLayers,
      viewLayerList = me.getViewLayers()

    // Deprecated : Replace by yaml config in center parameters
    _.extend(BASE_LAYERS.road, deprecatedRoadLayer())
    _.extend(BASE_LAYERS.marine, deprecatedMarineLayer())
    _.extend(BASE_LAYERS.imagery, deprecatedImageryLayer())
    _.extend(BASE_LAYERS.hybrid, deprecatedHybridLayer())
    _.extend(BASE_LAYERS.bathymetry, deprecatedBathymetryLayer())
    _.extend(BASE_LAYERS.southPoleCCAMLR, deprecatedSouthPoleCCAMLRLayer())
    _.extend(BASE_LAYERS.northPole, deprecatedNorthPoleLayer())
    _.extend(BASE_LAYERS.southPole, deprecatedSouthPoleLayer())

    // Read layer config from yaml in center parameters
    me.addBaseLayerFromYaml('road', 'yamlRoad')
    me.addBaseLayerFromYaml('marine', 'yamlMarine')
    me.addBaseLayerFromYaml('imagery', 'yamlImagery')
    me.addBaseLayerFromYaml('hybrid', 'yamlHybrid')
    me.addBaseLayerFromYaml('bathymetry', 'yamlBathymetry')
    me.addBaseLayerFromYaml('southPoleCCAMLR', 'yamlSouthPoleCCAMLR')
    me.addBaseLayerFromYaml('northPole', 'yamlNorthPole')
    me.addBaseLayerFromYaml('southPole', 'yamlSouthPole')
    me.addBaseLayerFromYaml('roadEvfr')
    me.addBaseLayerFromYaml('roadOsni')
    me.addBaseLayerFromYaml('ordonanceSurveyUk')
    me.addBaseLayerFromYaml('admiralty')

    // On conserve que les layers autorisés
    baseLayers = _.pick(BASE_LAYERS, rightsLayers)

    // On positionne la visibilité des layers à partir de la préférence
    _.each(baseLayers, (config, id) => {
      config.visible = baseLayerPref === id
      config.id = id
      if (!config.data) {
        config.data = {}
      }
      config.data.label =
        config.data.label || config.label || _.upperFirst(config.id) // Pour le I18n
    })

    let baseLayerList = _.values(baseLayers)

    // Ensure that at least one base or view layer is visible
    if (
      !_.find(baseLayerList.concat(viewLayerList), { visible: true }) &&
      baseLayerList.length
    ) {
      baseLayerList[0].visible = true
    }

    return baseLayerList
  },

  addBaseLayerFromYaml: function (id, rightId) {
    const config = loadLayerConfigFromYaml(
      id,
      RIGHTS('map.layers.' + (rightId || id), ''),
    )
    if (config) {
      BASE_LAYERS[id] = config
    }
  },

  /**
   * Retourne la configuration pour les layers de fonds de type vue (google) : viewLayers.
   * @returns {*|Object}
   */
  getViewLayers: function () {
    let rightsLayers = RIGHTS('map.baseLayers', 'road').split(','), // Base Layers autorisés
      baseLayerPref = THM_PREF.get('baseLayer') || 'none', // Préférence pointant vers le dernier baseLayer actif
      viewLayers

    // On conserve que les layers autorisés
    viewLayers = _.pick(VIEW_LAYERS, rightsLayers)

    // On positionne la visibilité des layers à partir de la préférence
    _.each(viewLayers, (config, id) => {
      config.visible = baseLayerPref === id
      config.id = id
    })

    return _.values(viewLayers)
  },

  /**
   * Retourne la configuration pour les layers informatifs : overlayLayers.
   * @returns {*|Object}
   */
  getOverlayLayers: function () {
    let layers = [],
      rightsLayers = RIGHTS(
        'map.overlayLayers',
        'nightAndDay,grid,scaleLine,eez,ices,fao',
      ).split(','),
      overlayLayers

    if (RIGHTS('data.geoTiff.authorized') && RIGHTS('data.geoimages.accept')) {
      rightsLayers.push('geotiffs')
    }

    overlayLayers = _.filter(
      OVERLAY_LAYERS,
      (layer) => rightsLayers.indexOf(layer.id || layer) >= 0,
    )

    for (let layer of overlayLayers) {
      if (layer.type === 'tileWMS' || layer.type === 'tileWMTS') {
        layer.visible = THM_PREF.get('wmsLayer-' + layer.id)
        layer.url = RIGHTS('map.layers.geoserver.url')
        layers.push(layer)
      } else if (layer.type === 'group') {
        layer.visible = true
        layers.push(layer)
      } else {
        layers.push({
          id: layer,
          visible: THM_PREF.get('overlayLayer-' + layer),
        })
      }
    }

    return layers
  },

  /**
   * Retourne la configuration pour les layers métier : vectorLayers.
   * @returns {*|Object}
   */
  getVectorLayers: function () {
    return VECTOR_LAYERS
  },

  /**
   * Returns sub layers for a given layer
   */
  getBaseLayersConf: function (layer) {
    let me = this

    return me[layer + 'Layers']
  },

  getTileSizeFromRight: function () {
    let me = this,
      url = RIGHTS('map.base.cmap.config'),
      right = url.split(':'),
      size = 512

    size = right[1] ? right[1] : size

    return size
  },
})

const MAP_CONFIG = new ThmUmvMapConfig()

export { MAP_CONFIG }
export default ThmUmvMapConfig
